// ShopManager.jsx — admin for the Nova Skincare shop.
// Tabs: Products · Orders · Insights (conversion funnel + abandoned carts) · Settings
import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", border:"#e8e4df", light:"#f3f4f6",
  gold:"#b48c3c", goldD:"#8a6f2e", goldBg:"rgba(180,140,60,0.08)",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)", red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", purple:"#7c3aed", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const inp = {width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};
const money = (n)=>"$"+Number(n||0).toFixed(2);
const SHOP_URL = "https://www.elyonhq.com/shop";

export default function ShopManager() {
  const [tab, setTab] = useState("products");
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:10,marginBottom:16}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>🛍️ Skincare Shop</div>
          <div style={{fontSize:12,color:C.muted}}>Manage products, orders & track conversions · <a href={SHOP_URL} target="_blank" rel="noreferrer" style={{color:C.gold}}>view storefront ↗</a></div>
        </div>
      </div>
      <div style={{display:"flex",gap:6,marginBottom:18,flexWrap:"wrap"}}>
        {[["products","🧴 Products"],["orders","📦 Orders"],["insights","📈 Insights"],["settings","⚙️ Settings"]].map(([id,l])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:"9px 16px",borderRadius:9,border:"1px solid "+(tab===id?C.gold:C.border),background:tab===id?C.gold:C.surface,color:tab===id?"#fff":C.text,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:F}}>{l}</button>
        ))}
      </div>
      {tab==="products" && <Products/>}
      {tab==="orders" && <Orders/>}
      {tab==="insights" && <Insights/>}
      {tab==="settings" && <Settings/>}
    </div>
  );
}

// ── PRODUCTS ──
function Products() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await supabase.from("store_products").select("*").order("order_index").order("created_at",{ascending:false}); setItems(data||[]); };
  useEffect(()=>{ load(); },[]);
  return (
    <div>
      <button onClick={()=>setEditing({ active:true, can_ship:true, can_pickup:true, track_stock:true, stock_qty:0, price:0 })} style={{marginBottom:14,padding:"10px 18px",background:C.gold,color:"#fff",border:"none",borderRadius:9,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:F}}>+ Add Product</button>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
        {items.length===0 && <div style={{color:C.muted,padding:30}}>No products yet — add your first one ✨</div>}
        {items.map(p=>(
          <div key={p.id} onClick={()=>setEditing(p)} style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden",cursor:"pointer",opacity:p.active?1:0.55}}>
            <div style={{aspectRatio:"16/10",background:C.light,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
              {p.image_url ? <img src={p.image_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:34,opacity:0.3}}>🧴</span>}
            </div>
            <div style={{padding:"11px 13px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                {!p.active && <span style={{fontSize:9,padding:"2px 6px",background:C.light,color:C.muted,borderRadius:5,fontWeight:700}}>HIDDEN</span>}
              </div>
              <div style={{fontSize:12,color:C.muted,marginTop:3}}>{p.category||"—"}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                <span style={{fontSize:15,fontWeight:800,color:C.text}}>{money(p.price)}</span>
                <span style={{fontSize:11,fontWeight:700,color:p.track_stock?((p.stock_qty>0)?C.green:C.red):C.muted}}>{p.track_stock?`${p.stock_qty} in stock`:"∞ stock"}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <ProductEditor product={editing} onClose={()=>setEditing(null)} onSaved={()=>{setEditing(null);load();}}/>}
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [p, setP] = useState(product);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k,v)=>setP(prev=>({...prev,[k]:v}));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Pick an image file"); return; }
    if (file.size > 5*1024*1024) { setErr("Image must be under 5MB"); return; }
    setErr(""); setUploading(true);
    try {
      const ext = (file.name.split(".").pop()||"jpg").toLowerCase();
      const path = `product-${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
      const { error } = await supabase.storage.from("product-photos").upload(path, file, { cacheControl:"3600", upsert:false });
      if (error) throw error;
      const { data: pub } = supabase.storage.from("product-photos").getPublicUrl(path);
      set("image_url", pub.publicUrl);
    } catch(e){ setErr(e.message||"Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!p.name?.trim()) { setErr("Name is required"); return; }
    setSaving(true); setErr("");
    const row = {
      name:p.name, brand:p.brand||null, category:p.category||null,
      short_description:p.short_description||null, description:p.description||null,
      price:Number(p.price)||0, compare_at_price:p.compare_at_price?Number(p.compare_at_price):null,
      image_url:p.image_url||null, sku:p.sku||null,
      track_stock:!!p.track_stock, stock_qty:parseInt(p.stock_qty)||0,
      can_ship:p.can_ship!==false, can_pickup:p.can_pickup!==false,
      active:p.active!==false, featured:!!p.featured, order_index:parseInt(p.order_index)||0,
      updated_at:new Date().toISOString(),
    };
    try {
      if (p.id) await supabase.from("store_products").update(row).eq("id",p.id);
      else await supabase.from("store_products").insert(row);
      onSaved();
    } catch(e){ setErr(e.message||"Save failed"); setSaving(false); }
  };
  const del = async () => { if(!p.id||!window.confirm("Delete this product?"))return; await supabase.from("store_products").delete().eq("id",p.id); onSaved(); };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:2000,display:"flex",justifyContent:"center",alignItems:"flex-start",padding:"30px 16px",overflow:"auto"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:16,maxWidth:560,width:"100%",padding:24}}>
        <div style={{fontSize:19,fontWeight:800,color:C.text,fontFamily:S,marginBottom:16}}>{p.id?"Edit Product":"New Product"}</div>
        <div style={{display:"flex",gap:14,marginBottom:14}}>
          <div style={{width:110,height:110,borderRadius:10,background:C.light,flexShrink:0,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
            {p.image_url ? <img src={p.image_url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:30,opacity:0.3}}>🧴</span>}
          </div>
          <div style={{flex:1}}>
            <label style={{display:"inline-block",padding:"8px 14px",background:C.goldBg,color:C.goldD,border:"1px solid "+C.gold+"40",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer"}}>
              {uploading?"Uploading…":"📷 Upload photo"}
              <input type="file" accept="image/*" onChange={upload} style={{display:"none"}}/>
            </label>
            <div style={{fontSize:11,color:C.muted,marginTop:6}}>Square images look best. Under 5MB.</div>
            <input value={p.image_url||""} onChange={e=>set("image_url",e.target.value)} placeholder="…or paste an image URL" style={{...inp,marginTop:8,fontSize:12}}/>
          </div>
        </div>
        <Lbl t="Product name"/><input value={p.name||""} onChange={e=>set("name",e.target.value)} style={inp}/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
          <div><Lbl t="Brand"/><input value={p.brand||""} onChange={e=>set("brand",e.target.value)} style={inp}/></div>
          <div><Lbl t="Category"/><input value={p.category||""} onChange={e=>set("category",e.target.value)} placeholder="e.g. Cleansers" style={inp}/></div>
        </div>
        <div style={{marginTop:10}}><Lbl t="Short description (shown on card)"/><input value={p.short_description||""} onChange={e=>set("short_description",e.target.value)} style={inp}/></div>
        <div style={{marginTop:10}}><Lbl t="Full description"/><textarea value={p.description||""} onChange={e=>set("description",e.target.value)} rows={3} style={{...inp,resize:"vertical"}}/></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
          <div><Lbl t="Price ($)"/><input type="number" step="0.01" value={p.price||""} onChange={e=>set("price",e.target.value)} style={inp}/></div>
          <div><Lbl t="Compare-at (was) price"/><input type="number" step="0.01" value={p.compare_at_price||""} onChange={e=>set("compare_at_price",e.target.value)} style={inp}/></div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
          <div><Lbl t="SKU (optional)"/><input value={p.sku||""} onChange={e=>set("sku",e.target.value)} style={inp}/></div>
          <div><Lbl t="Stock qty"/><input type="number" value={p.stock_qty??0} onChange={e=>set("stock_qty",e.target.value)} disabled={!p.track_stock} style={{...inp,opacity:p.track_stock?1:0.5}}/></div>
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:14,marginTop:14}}>
          <Chk label="Track stock" v={p.track_stock!==false} onChange={v=>set("track_stock",v)}/>
          <Chk label="Can ship" v={p.can_ship!==false} onChange={v=>set("can_ship",v)}/>
          <Chk label="Pickup ok" v={p.can_pickup!==false} onChange={v=>set("can_pickup",v)}/>
          <Chk label="Active (visible)" v={p.active!==false} onChange={v=>set("active",v)}/>
          <Chk label="Featured" v={!!p.featured} onChange={v=>set("featured",v)}/>
        </div>
        {err && <div style={{color:C.red,fontSize:13,marginTop:12}}>{err}</div>}
        <div style={{display:"flex",gap:8,marginTop:18,justifyContent:"space-between"}}>
          {p.id ? <button onClick={del} style={{padding:"10px 16px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:F}}>Delete</button> : <span/>}
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose} style={{padding:"10px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
            <button onClick={save} disabled={saving} style={{padding:"10px 20px",background:C.gold,color:"#fff",border:"none",borderRadius:8,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:F}}>{saving?"Saving…":"Save Product"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ORDERS ──
function Orders() {
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState({});
  const [open, setOpen] = useState(null);
  const load = async () => {
    const { data } = await supabase.from("shop_orders").select("*").order("created_at",{ascending:false}).limit(200);
    setOrders(data||[]);
  };
  useEffect(()=>{ load(); },[]);
  const loadItems = async (id) => { if(items[id])return; const { data } = await supabase.from("shop_order_items").select("*").eq("order_id",id); setItems(prev=>({...prev,[id]:data||[]})); };
  const setStatus = async (id,status) => { await supabase.from("shop_orders").update({status,updated_at:new Date().toISOString()}).eq("id",id); load(); };

  const STATUSES = ["paid","fulfilled","shipped","picked_up","cancelled","refunded"];
  const badge = (s)=>({paid:C.green,fulfilled:C.blue,shipped:C.blue,picked_up:C.green,pending:C.dim,cancelled:C.red,refunded:C.red}[s]||C.muted);
  const paid = orders.filter(o=>o.status!=="pending");
  const revenue = paid.filter(o=>!["cancelled","refunded"].includes(o.status)).reduce((s,o)=>s+Number(o.total||0),0);

  return (
    <div>
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <Stat label="Paid Orders" value={paid.length} color={C.green}/>
        <Stat label="Revenue" value={money(revenue)} color={C.gold}/>
      </div>
      {paid.length===0 && <div style={{color:C.muted,padding:30}}>No orders yet.</div>}
      {paid.map(o=>(
        <div key={o.id} style={{background:C.surface,border:"1px solid "+C.border,borderRadius:11,marginBottom:10,overflow:"hidden"}}>
          <div onClick={()=>{const n=open===o.id?null:o.id;setOpen(n);if(n)loadItems(o.id);}} style={{padding:"13px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",gap:10,flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:14,fontWeight:800,color:C.text}}>{o.order_number} · {o.customer_name||"Guest"}</div>
              <div style={{fontSize:12,color:C.muted}}>{new Date(o.created_at).toLocaleString()} · {o.fulfillment_type==="ship"?"📦 Ship":"🏬 Pickup"}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:15,fontWeight:800,color:C.text}}>{money(o.total)}</span>
              <span style={{fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:99,background:badge(o.status)+"20",color:badge(o.status),textTransform:"uppercase"}}>{o.status}</span>
            </div>
          </div>
          {open===o.id && (
            <div style={{padding:"0 16px 16px",borderTop:"1px solid "+C.border}}>
              <div style={{padding:"12px 0",fontSize:13,color:C.text}}>
                {(items[o.id]||[]).map(it=>(
                  <div key={it.id} style={{display:"flex",justifyContent:"space-between",marginBottom:5}}><span>{it.product_name} × {it.qty}</span><span style={{fontWeight:700}}>{money(it.line_total)}</span></div>
                ))}
                <div style={{borderTop:"1px solid "+C.border,marginTop:8,paddingTop:8,fontSize:12,color:C.muted}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
                  {Number(o.shipping_fee)>0 && <div style={{display:"flex",justifyContent:"space-between"}}><span>Shipping</span><span>{money(o.shipping_fee)}</span></div>}
                  {Number(o.tax)>0 && <div style={{display:"flex",justifyContent:"space-between"}}><span>Tax</span><span>{money(o.tax)}</span></div>}
                </div>
              </div>
              <div style={{fontSize:12.5,color:C.muted,marginBottom:10,lineHeight:1.6}}>
                📧 {o.customer_email||"—"} · 📞 {o.customer_phone||"—"}<br/>
                {o.fulfillment_type==="ship" && o.shipping_address && <span>📦 {[o.shipping_address.line1,o.shipping_address.line2,o.shipping_address.city,o.shipping_address.state,o.shipping_address.zip].filter(Boolean).join(", ")}</span>}
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {STATUSES.map(s=>(
                  <button key={s} onClick={()=>setStatus(o.id,s)} style={{padding:"6px 12px",borderRadius:7,border:"1px solid "+(o.status===s?badge(s):C.border),background:o.status===s?badge(s)+"15":C.surface,color:o.status===s?badge(s):C.muted,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,textTransform:"capitalize"}}>{s.replace("_"," ")}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── INSIGHTS ──
function Insights() {
  const [events, setEvents] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ (async()=>{
    setLoading(true);
    const since = new Date(Date.now()-days*864e5).toISOString();
    const { data } = await supabase.from("shop_events").select("*").gte("created_at",since).order("created_at",{ascending:false}).limit(5000);
    setEvents(data||[]); setLoading(false);
  })(); },[days]);

  if (loading) return <div style={{color:C.muted,padding:20}}>Loading…</div>;

  const count = (t)=>events.filter(e=>e.event_type===t).length;
  const sessions = new Set(events.map(e=>e.session_id).filter(Boolean));
  const addedSessions = new Set(events.filter(e=>e.event_type==="add_to_cart").map(e=>e.session_id));
  const purchasedSessions = new Set(events.filter(e=>e.event_type==="purchase").map(e=>e.session_id));
  const checkoutSessions = new Set(events.filter(e=>e.event_type==="begin_checkout").map(e=>e.session_id));
  const revenue = events.filter(e=>e.event_type==="purchase").reduce((s,e)=>s+Number(e.value||0),0);
  const convRate = sessions.size ? Math.round((purchasedSessions.size/sessions.size)*1000)/10 : 0;
  const cartAbandonRate = checkoutSessions.size ? Math.round(((checkoutSessions.size-purchasedSessions.size)/checkoutSessions.size)*1000)/10 : 0;

  // Abandoned: latest begin_checkout per session with NO purchase
  const abandoned = [];
  const seen = new Set();
  for (const e of events.filter(e=>e.event_type==="begin_checkout")) {
    if (seen.has(e.session_id)) continue; seen.add(e.session_id);
    if (!purchasedSessions.has(e.session_id)) abandoned.push(e);
  }

  const funnel = [
    ["Visited shop", sessions.size, C.blue],
    ["Viewed a product", new Set(events.filter(e=>e.event_type==="view_product").map(e=>e.session_id)).size, C.purple],
    ["Added to cart", addedSessions.size, C.gold],
    ["Started checkout", checkoutSessions.size, "#e8851f"],
    ["Purchased", purchasedSessions.size, C.green],
  ];
  const top = funnel[0][1] || 1;

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:14}}>
        {[7,30,90].map(d=><button key={d} onClick={()=>setDays(d)} style={{padding:"6px 14px",borderRadius:8,border:"1px solid "+(days===d?C.gold:C.border),background:days===d?C.gold:C.surface,color:days===d?"#fff":C.text,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>{d}d</button>)}
      </div>
      <div style={{display:"flex",gap:10,marginBottom:18,flexWrap:"wrap"}}>
        <Stat label="Revenue" value={money(revenue)} color={C.gold}/>
        <Stat label="Purchases" value={purchasedSessions.size} color={C.green}/>
        <Stat label="Conversion" value={convRate+"%"} color={C.blue}/>
        <Stat label="Cart Abandon" value={cartAbandonRate+"%"} color={C.red}/>
      </div>

      <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:18,marginBottom:18}}>
        <div style={{fontSize:13,fontWeight:800,color:C.text,marginBottom:14}}>Conversion Funnel</div>
        {funnel.map(([label,n,color])=>(
          <div key={label} style={{marginBottom:11}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,marginBottom:4}}><span style={{color:C.text,fontWeight:600}}>{label}</span><span style={{color:C.muted,fontWeight:700}}>{n}{top>0 && label!=="Visited shop" ? ` · ${Math.round((n/top)*100)}%`:""}</span></div>
            <div style={{height:10,background:C.light,borderRadius:6,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.max(2,(n/top)*100)}%`,background:color,borderRadius:6}}/></div>
          </div>
        ))}
      </div>

      <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:18}}>
        <div style={{fontSize:13,fontWeight:800,color:C.text,marginBottom:6}}>🛒 Abandoned Carts <span style={{color:C.muted,fontWeight:600}}>({abandoned.length})</span></div>
        <div style={{fontSize:11.5,color:C.muted,marginBottom:12}}>Started checkout but didn't buy — prime for a follow-up.</div>
        {abandoned.length===0 && <div style={{color:C.dim,fontSize:13,padding:10}}>None 🎉</div>}
        {abandoned.slice(0,40).map((e,i)=>(
          <div key={i} style={{padding:"10px 0",borderBottom:"1px solid "+C.border,fontSize:13}}>
            <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
              <span style={{fontWeight:700,color:C.text}}>{e.customer_email || e.customer_phone || "Anonymous visitor"}</span>
              <span style={{color:C.gold,fontWeight:700}}>{money(e.value)}</span>
            </div>
            <div style={{color:C.muted,fontSize:12,marginTop:3}}>
              {new Date(e.created_at).toLocaleString()} · {(e.cart_snapshot||[]).map(c=>`${c.name} ×${c.qty}`).join(", ")||"—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SETTINGS ──
function Settings() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(()=>{ (async()=>{ const { data } = await supabase.from("store_settings").select("*").eq("id",1).single(); setS(data||{id:1}); })(); },[]);
  const set = (k,v)=>setS(prev=>({...prev,[k]:v}));
  const save = async () => {
    await supabase.from("store_settings").update({
      shop_enabled:s.shop_enabled!==false, ship_enabled:s.ship_enabled!==false, pickup_enabled:s.pickup_enabled!==false,
      shipping_fee:Number(s.shipping_fee)||0, free_ship_threshold:s.free_ship_threshold===""||s.free_ship_threshold==null?null:Number(s.free_ship_threshold),
      tax_rate:Number(s.tax_rate)||0, pickup_address:s.pickup_address||null,
      hero_heading:s.hero_heading||null, hero_subheading:s.hero_subheading||null,
      updated_at:new Date().toISOString(),
    }).eq("id",1);
    setSaved(true); setTimeout(()=>setSaved(false),2000);
  };
  if (!s) return <div style={{color:C.muted,padding:20}}>Loading…</div>;
  return (
    <div style={{maxWidth:520}}>
      <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:20}}>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Chk label="Shop is live" v={s.shop_enabled!==false} onChange={v=>set("shop_enabled",v)}/>
          <div style={{display:"flex",gap:18}}>
            <Chk label="Offer shipping" v={s.ship_enabled!==false} onChange={v=>set("ship_enabled",v)}/>
            <Chk label="Offer pickup" v={s.pickup_enabled!==false} onChange={v=>set("pickup_enabled",v)}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div><Lbl t="Shipping fee ($)"/><input type="number" step="0.01" value={s.shipping_fee??""} onChange={e=>set("shipping_fee",e.target.value)} style={inp}/></div>
            <div><Lbl t="Free shipping over ($)"/><input type="number" step="0.01" value={s.free_ship_threshold??""} onChange={e=>set("free_ship_threshold",e.target.value)} placeholder="blank = never" style={inp}/></div>
          </div>
          <div><Lbl t="Sales tax rate (e.g. 0.08625 = 8.625%)"/><input type="number" step="0.0001" value={s.tax_rate??""} onChange={e=>set("tax_rate",e.target.value)} placeholder="0 = no tax" style={inp}/></div>
          <div><Lbl t="Pickup address"/><input value={s.pickup_address||""} onChange={e=>set("pickup_address",e.target.value)} style={inp}/></div>
          <div><Lbl t="Storefront heading"/><input value={s.hero_heading||""} onChange={e=>set("hero_heading",e.target.value)} style={inp}/></div>
          <div><Lbl t="Storefront subheading"/><input value={s.hero_subheading||""} onChange={e=>set("hero_subheading",e.target.value)} style={inp}/></div>
          <button onClick={save} style={{padding:"12px",background:C.gold,color:"#fff",border:"none",borderRadius:9,fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:F}}>{saved?"✓ Saved":"Save Settings"}</button>
        </div>
      </div>
    </div>
  );
}

// ── helpers ──
function Lbl({ t }) { return <label style={{display:"block",fontSize:10.5,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{t}</label>; }
function Chk({ label, v, onChange }) {
  return (
    <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,fontWeight:600,color:C.text}}>
      <span onClick={()=>onChange(!v)} style={{width:42,height:23,borderRadius:12,background:v?C.green:"#ccc",position:"relative",transition:"all .2s",flexShrink:0}}>
        <span style={{position:"absolute",top:2,left:v?21:2,width:19,height:19,borderRadius:10,background:"#fff",transition:"all .2s"}}/>
      </span>
      {label}
    </label>
  );
}
function Stat({ label, value, color }) {
  return <div style={{flex:"1 1 130px",padding:12,background:C.surface,border:"1px solid "+C.border,borderRadius:10,borderLeft:"3px solid "+color}}><div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</div><div style={{fontSize:19,fontWeight:800,color:C.text,fontFamily:S,marginTop:3}}>{value}</div></div>;
}
